const { ObjTypes, TxnTypes } = require('./types')
const qboMapping = require('./qbo')
const entryMapping = require('./txn-entry')

async function genSyncId (pgClient) {
  const { rows: [{ syncId }] } = await pgClient.query(`
    select nextval('fin.sync_id_seq') as "syncId"
  `)
  return syncId
}

async function insertRawQbo (pgClient, syncId, companyCode, objsByType) {
  const idsByType = {}
  for (const [type, objs] of Object.entries(objsByType)) {
    let ids
    if (type === ObjTypes.account) {
      ids = await insertRawQboAccount(pgClient, syncId, companyCode, objs)
    } else if (type === ObjTypes.customer) {
      ids = await insertRawQboCustomer(pgClient, syncId, companyCode, objs)
    } else if (TxnTypes.includes(type)) {
      ids = await insertRawQboTxn(pgClient, syncId, companyCode, type, objs)
    } else {
      throw new Error(`Unknown object type: ${type}`)
    }
    idsByType[type] = ids
  }
  return idsByType
}

class QueryValues {
  constructor () {
    this.placeholderGroups = []
    this.values = []
  }

  push (...values) {
    const len = this.values.length
    if (values.length === 0) {
      return
    }
    const parts = [...new Array(values.length).keys()].map((_, i) => `$${len + i + 1}`)
    const placeholders = '(' + parts.join(',') + ')'
    this.placeholderGroups.push(placeholders)
    this.values.push(...values)
  }

  getPlaceholders () {
    return this.placeholderGroups.join(',')
  }
}

async function insertRawQboAccount (
  pgClient,
  syncId,
  companyCode,
  accounts
) {
  const queryValues = new QueryValues()
  for (const account of accounts) {
    const accountId = parseInt(account.Id)
    const syncToken = account.SyncToken && parseInt(account.SyncToken)
    const deleted = account.status === 'Deleted'
    queryValues.push(
      syncId,
      companyCode,
      accountId,
      syncToken,
      deleted,
      JSON.stringify(account)
    )
  }
  const text = `
    INSERT INTO fin.raw_qbo_account AS t (
      sync_id, company, account_id, sync_token, deleted, data
    )
    VALUES ${queryValues.getPlaceholders()}
    ON CONFLICT (company, account_id) DO
    UPDATE SET updated_at = excluded.updated_at
             , data = excluded.data
             , sync_token = excluded.sync_token
             , deleted = excluded.deleted
             , sync_id = excluded.sync_id
    WHERE excluded.sync_token >= t.sync_token OR excluded.deleted
    RETURNING 'qbo' as origin
            , company
            , account_id as "accountId"
  `
  const { rows } = await pgClient.query(text, queryValues.values)
  return rows
}

async function insertRawQboCustomer (
  pgClient,
  syncId,
  companyCode,
  customers
) {
  const queryValues = new QueryValues()
  for (const customer of customers) {
    const customerId = parseInt(customer.Id)
    const syncToken = customer.SyncToken && parseInt(customer.SyncToken)
    const deleted = customer.status === 'Deleted'
    queryValues.push(
      syncId,
      companyCode,
      customerId,
      syncToken,
      deleted,
      JSON.stringify(customer)
    )
  }
  const text = `
    INSERT INTO fin.raw_qbo_customer AS t (
      sync_id, company, customer_id, sync_token, deleted, data
    )
    VALUES ${queryValues.getPlaceholders()}
    ON CONFLICT (company, customer_id) DO
    UPDATE SET updated_at = excluded.updated_at
             , data = excluded.data
             , sync_token = excluded.sync_token
             , deleted = excluded.deleted
             , sync_id = excluded.sync_id
    WHERE excluded.sync_token >= t.sync_token OR excluded.deleted
    RETURNING 'qbo' as origin
            , company
            , customer_id as "customerId"
  `
  const { rows } = await pgClient.query(text, queryValues.values)
  return rows
}

async function insertRawQboTxn (
  pgClient,
  syncId,
  companyCode,
  txnType,
  txns
) {
  const queryValues = new QueryValues()
  for (const txn of txns) {
    const txnId = parseInt(txn.Id)
    const syncToken = txn.SyncToken && parseInt(txn.SyncToken)
    const deleted = txn.status === 'Deleted'
    queryValues.push(
      syncId,
      companyCode,
      txnType,
      txnId,
      syncToken,
      deleted,
      JSON.stringify(txn)
    )
  }
  const text = `
    INSERT INTO fin.raw_qbo_txn AS t (
      sync_id, company, txn_type, txn_id, sync_token, deleted, data
    )
    VALUES ${queryValues.getPlaceholders()}
    ON CONFLICT (company, txn_type, txn_id) DO
    UPDATE SET updated_at = excluded.updated_at
             , data = excluded.data
             , sync_token = excluded.sync_token
             , deleted = excluded.deleted
             , sync_id = excluded.sync_id
    WHERE excluded.sync_token >= t.sync_token OR excluded.deleted
    RETURNING 'qbo' as origin
            , company
            , txn_type as "txnType"
            , txn_id as "txnId"
  `
  const { rows } = await pgClient.query(text, queryValues.values)
  return rows
}

async function transformQbo (pgClient, idsByType) {
  for (const [type, ids] of Object.entries(idsByType)) {
    if (type === ObjTypes.account) {
      return transformQboAccount(pgClient, ids)
    } else if (type === ObjTypes.customer) {
      return transformQboCustomer(pgClient, ids)
    } else if (TxnTypes.includes(type)) {
      return transformQboTxn(pgClient, type, ids)
    } else {
      throw new Error(`Unknown object type: ${type}`)
    }
  }
}

async function transformQboAccount (pgClient, accounts) {
  const m = qboMapping[ObjTypes.account]
  const queryValues = new QueryValues()
  for (const { origin, company, accountId } of accounts) {
    queryValues.push(origin, company, accountId)
  }
  const placeholders = queryValues.getPlaceholders()
  const values = queryValues.values
  const argsCte = `
    args AS (
        SELECT origin::fin.origin_type as origin
             , company::fin.company_type as company
             , account_id::integer as account_id
        FROM ( VALUES ${placeholders} ) v(origin, company, account_id)
    )
  `
  await pgClient.query('BEGIN')
  await pgClient.query(`
    WITH ${argsCte}
    DELETE FROM fin.account
    WHERE (origin, company, account_id) IN (SELECT * FROM args)
    RETURNING *
  `, values)
  const query = `
    WITH ${argsCte},
    raw AS (
        SELECT *
        FROM fin.raw_qbo_account
        WHERE (origin, company, account_id) IN (SELECT * FROM args)
          AND NOT deleted
    ),
    acc AS (
      ${m.main}
      RETURNING *
    )
    SELECT origin, company, account_id as "accountId" FROM acc;
  `
  const { rows } = await pgClient.query(query, values)
  await pgClient.query('COMMIT')
  return rows
}

async function transformQboCustomer (pgClient, customers) {
  const m = qboMapping[ObjTypes.customer]
  const queryValues = new QueryValues()
  for (const { origin, company, customerId } of customers) {
    queryValues.push(origin, company, customerId)
  }
  const placeholders = queryValues.getPlaceholders()
  const values = queryValues.values
  const argsCte = `
    args AS (
        SELECT origin::fin.origin_type as origin
             , company::fin.company_type as company
             , customer_id::integer as customer_id
        FROM ( VALUES ${placeholders} ) v(origin, company, customer_id)
    )
  `
  await pgClient.query('BEGIN')
  await pgClient.query(`
    WITH ${argsCte}
    DELETE FROM fin.customer
    WHERE (origin, company, customer_id) IN (SELECT * FROM args)
    returning *
  `, values)
  const query = `
    WITH ${argsCte},
    raw AS (
        SELECT *
        FROM fin.raw_qbo_customer
        WHERE (origin, company, customer_id) IN (SELECT * FROM args)
          AND NOT deleted
    ),
    acc AS (
      ${m.main}
      RETURNING *
    )
    SELECT origin, company, customer_id as "customerId" FROM acc;
  `
  const { rows } = await pgClient.query(query, values)
  await pgClient.query('COMMIT')
  return rows
}

async function getQboRawTxnCustomerIds (pgClient, syncId) {
  const { rows } = await pgClient.query(`
    with raw AS (
        SELECT origin, company, data
        FROM fin.raw_qbo_txn
        WHERE sync_id = $1
    )
    SELECT origin, company, customer_id as "customerId"
    FROM (
      (${qboMapping.invoice.rawCustomerIds})
      UNION
      (${qboMapping.creditmemo.rawCustomerIds})
      UNION
      (${qboMapping.deposit.rawCustomerIds})
      UNION
      (${qboMapping.journalentry.rawCustomerIds})
      UNION
      (${qboMapping.payment.rawCustomerIds})
      UNION
      (${qboMapping.purchase.rawCustomerIds})
    ) o
    WHERE customer_id IS NOT NULL
    GROUP BY 1, 2, 3
  `, [syncId])
  return rows
}

async function getQboRawTxnAccountIds (pgClient, syncId) {
  const { rows } = await pgClient.query(`
    with raw as (
      SELECT origin, company, data
      FROM fin.raw_qbo_txn
      WHERE sync_id = $1
    )
    SELECT origin, company, account_id as "accountId"
    from (
      (${qboMapping.creditmemo.rawAccountIds})
      UNION
      (${qboMapping.deposit.rawAccountIds})
      UNION
      (${qboMapping.invoice.rawAccountIds})
      UNION
      (${qboMapping.journalentry.rawAccountIds})
      UNION
      (${qboMapping.purchase.rawAccountIds})
    ) a
    where account_id is not null
    group by 1, 2, 3
  `, [syncId])
  return rows
}

async function transformQboTxn (pgClient, txnType, txns) {
  const qm = qboMapping[txnType]
  const em = entryMapping[txnType]
  if (!qm) {
    throw new Error(`Mapping not found for: ${txnType}`)
  }
  const queryValues = new QueryValues()
  for (const { origin, company, txnType, txnId } of txns) {
    queryValues.push(origin, company, txnType, txnId)
  }
  const placeholders = queryValues.getPlaceholders()
  const values = queryValues.values
  const argsCte = `
    args AS (
        SELECT origin::fin.origin_type as origin
             , company::fin.company_type as company
             , txn_type::fin.txn_type as txn_type
             , txn_id::integer as txn_id
        FROM ( VALUES ${placeholders} ) v(origin, company, txn_type, txn_id)
    )
  `
  await pgClient.query('BEGIN')
  await pgClient.query(`
    WITH ${argsCte}
    DELETE FROM fin.txn
    WHERE (origin, company, txn_type, txn_id) IN (SELECT * FROM args)
    returning *
  `, values)
  const query = `
    WITH ${argsCte},
    raw AS (
        SELECT origin, company, txn_type, txn_id, data
             , (data->>'TxnDate')::Date as txn_date
             , (data->'MetaData'->>'CreateTime')::TIMESTAMPTZ as created_at
             , (data->'MetaData'->>'LastUpdatedTime')::TIMESTAMPTZ as updated_at
        FROM fin.raw_qbo_txn
        WHERE (origin, company, txn_type, txn_id) IN (SELECT * FROM args)
          AND NOT deleted
    ),
    txn AS (
        INSERT INTO fin.txn (
            origin, company, txn_type, txn_id, txn_date, created_at, updated_at
        )
        SELECT origin
             , company
             , txn_type
             , txn_id
             , txn_date
             , created_at
             , updated_at
        FROM raw
        RETURNING origin, company, txn_type, txn_id, txn_date
    ),
    main AS (
      ${qm.main}
      RETURNING *
    ),
    line AS (
      ${qm.line}
      RETURNING *
    ),
    entry AS (
      ${em}
    )
    SELECT origin, company, txn_type as "txnType", txn_id as "txnId" FROM txn;
  `
  const { rows } = await pgClient.query(query, values)
  await pgClient.query('COMMIT')
  return rows
}

async function copyQboToFin (pgPool, { objTypes = Object.keys(ObjTypes) } = {}) {
  const pgClient = await pgPool.connect()
  for (const ot of objTypes) {
    const objType = ot.toLowerCase()
    if (objType === ObjTypes.account) {
      await pgClient.query(`
        INSERT INTO fin.raw_qbo_account AS t (
            company, account_id, sync_token, deleted, data
        )
        SELECT upper(company_code::text)::fin.company_type
             , entity_id, sync_token, deleted, data
        FROM qbo.account_raw
        ON CONFLICT (company, account_id) DO
        UPDATE SET updated_at = excluded.updated_at
                 , data = excluded.data
                 , sync_token = excluded.sync_token
                 , deleted = excluded.deleted
        WHERE excluded.sync_token >= t.sync_token OR excluded.deleted
      `)
    } else if (objType === ObjTypes.customer) {
      await pgClient.query(`
        INSERT INTO fin.raw_qbo_customer AS t (
            company, customer_id, sync_token, deleted, data
        )
        SELECT upper(company_code::text)::fin.company_type
             , entity_id, sync_token, deleted, data
        FROM qbo.customer_raw
        ON CONFLICT (company, customer_id) DO
        UPDATE SET updated_at = excluded.updated_at
                 , data = excluded.data
                 , sync_token = excluded.sync_token
                 , deleted = excluded.deleted
        WHERE excluded.sync_token >= t.sync_token OR excluded.deleted
      `)
    } else if (TxnTypes.includes(objType)) {
      await pgClient.query(`
        INSERT INTO fin.raw_qbo_txn AS t (
            company, txn_type, txn_id, sync_token, deleted, data
        )
        SELECT upper(company_code::text)::fin.company_type
             , $1, entity_id, sync_token, deleted, data
        FROM qbo.${objType}_raw
        ON CONFLICT (company, txn_type, txn_id) DO
        UPDATE SET updated_at = excluded.updated_at
                 , data = excluded.data
                 , sync_token = excluded.sync_token
                 , deleted = excluded.deleted
        WHERE excluded.sync_token >= t.sync_token OR excluded.deleted
      `, [objType])
    } else {
      throw new Error(`Unknown object type: ${objType}`)
    }
  }
  await pgClient.end()
}

async function transformAllQbo (
  pgPool,
  {
    limit = 1000,
    objTypes = Object.values(ObjTypes)
  } = {}
) {
  for (const ot of objTypes) {
    const objType = ot.toLowerCase()
    if (TxnTypes.includes(objType)) {
      await transformAllQboTxn(pgPool, objType, { limit })
    } else if (objType === ObjTypes.account) {
      await transformAllQboAccounts(pgPool, { limit })
    } else if (objType === ObjTypes.customer) {
      await transformAllQboCustomers(pgPool, { limit })
    } else {
      throw new Error(`Unknown object type: ${objType}`)
    }
  }
}

async function transformAllQboTxn (pgPool, txnType, { limit = 1000 } = {}) {
  const pgClient = await pgPool.connect()
  let startId = 0
  while (true) {
    const { rows } = await pgClient.query(`
      SELECT origin, company, txn_type as "txnType", txn_id as "txnId"
      FROM fin.raw_qbo_txn
      WHERE txn_type = $1
      AND txn_id > $2
      ORDER BY txn_id
      LIMIT $3
    `, [txnType.toLowerCase(), startId, limit])
    if (rows.length > 0) {
      await transformQboTxn(pgClient, { [txnType]: rows })
      startId = rows[rows.length - 1].txnId
    }
    if (rows.length < limit) {
      break
    }
  }
  await pgClient.end()
}

async function transformAllQboAccounts (pgPool, { limit = 1000 } = {}) {
  const pgClient = await pgPool.connect()
  let startId = 0
  while (true) {
    const { rows } = await pgClient.query(`
      SELECT origin, company, account_id as "accountId"
      FROM fin.raw_qbo_account
      WHERE account_id > $1
      ORDER BY account_id
      LIMIT $2
    `, [startId, limit])
    if (rows.length > 0) {
      await transformQboAccount(pgClient, rows)
      startId = rows[rows.length - 1].accountId
    }
    if (rows.length < limit) {
      break
    }
  }
  await pgClient.end()
}

async function transformAllQboCustomers (pgPool, { limit = 1000 } = {}) {
  const pgClient = await pgPool.connect()
  let startId = 0
  while (true) {
    const { rows } = await pgClient.query(`
      SELECT origin, company, customer_id as "customerId"
      FROM fin.raw_qbo_customer
      WHERE customer_id > $1
      ORDER BY customer_id
      LIMIT $2
    `, [startId, limit])
    if (rows.length > 0) {
      await transformQboCustomer(pgClient, rows)
      startId = rows[rows.length - 1].customerId
    }
    if (rows.length < limit) {
      break
    }
  }
  await pgClient.end()
}

async function insertAndTransformRawQbo (pgClient, syncId, companyCode, objsByType) {
  for (const [objType, objs] of Object.entries(objsByType)) {
    if (!objs || !objs.length) {
      console.log({ code: 'No qbo objects received for sync', type: objType })
      continue
    }
    const ids = await insertRawQbo(pgClient, syncId, companyCode, { [objType]: objs })
    if (!ids[objType] || !ids[objType].length) {
      console.log({ code: 'No qbo objects received for transform', type: objType })
      continue
    }
    await transformQbo(pgClient, { [objType]: ids[objType] })
  }
}

async function syncQboObjs (pgConnection, companyCode, objsByType, qboEntitiesApi) {
  const useOwnClient = !('connection' in pgConnection)
  const pgClient = useOwnClient ? await pgConnection.connect() : pgConnection
  const syncId = await genSyncId(pgClient)
  await insertAndTransformRawQbo(pgClient, syncId, companyCode, objsByType)

  // Subsequently update balances for affected customers and accounts using syncId
  if (qboEntitiesApi) {
    const [affectedCustomers, affectedAccounts] = await Promise.all([
      getQboRawTxnCustomerIds(pgClient, syncId),
      getQboRawTxnAccountIds(pgClient, syncId)
    ])
    const [accounts, customers] = await Promise.all([
      qboEntitiesApi.account.getList(affectedAccounts.map(a => a.accountId), companyCode),
      qboEntitiesApi.customer.getList(affectedCustomers.map(c => c.customerId), companyCode)
    ])

    await insertAndTransformRawQbo(pgClient, syncId, companyCode, { account: accounts, customer: customers })
  }
  if (useOwnClient) {
    await pgClient.release()
  }
}

async function syncQbo (qbo, pgPool, companyCode, objType, { from, to } = {}) {
  const pgClient = await pgPool.connect()
  let where = null
  if (from || to) {
    if (TxnTypes.includes(objType)) {
      where = [
        (from && `TxnDate >= '${from}'`),
        (to && `TxnDate <= '${to}'`)
      ].filter(s => !!s).join('AND')
    } else {
      where = [
        (from && `MetaData.LastUpdatedTime >= '${from}'`),
        (to && `MetaData.LastUpdatedTime <= '${to}'`)
      ].filter(s => !!s).join('AND')
    }
  }

  let orderBy = null
  if (where) {
    orderBy = 'MetaData.LastUpdatedTime'
  }

  await qbo.entities.query.getAll({
    companyCode,
    type: objType,
    where,
    orderBy
  }, async function (objs) {
    console.log(objType, 'syncing', objs.length)
    const syncId = await genSyncId(pgClient)
    const ids = await insertRawQbo(pgClient, syncId, companyCode, { [objType]: objs })
    await transformQbo(pgClient, { [objType]: ids[objType] })
  })
  await pgClient.end()
}

module.exports = {
  ObjTypes,
  TxnTypes,
  genSyncId,
  insertRawQbo,
  transformQbo,
  syncQbo,
  syncQboObjs,
  copyQboToFin,
  transformAllQbo,
  getQboRawTxnCustomerIds
}
